import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CreditCard, Check, Crown, Zap, Building2, ArrowRight, Loader2, CalendarDays, Monitor, FolderKanban } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const MODULE_META = {
  event_center: { label: "Event Center", icon: CalendarDays, color: "#F59E0B" },
  web: { label: "Web", icon: Monitor, color: "#0099CC" },
  projects: { label: "Projects", icon: FolderKanban, color: "#10B981" },
};
const PLAN_ICONS = { free: Zap, pro: Crown, enterprise: Building2 };

export default function SubscriptionPage() {
  const { token, user } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [plans, setPlans] = useState(null);
  const [subs, setSubs] = useState([]);
  const [yearly, setYearly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [plansRes, subsRes] = await Promise.all([
        axios.get(`${API}/api/plans`),
        axios.get(`${API}/api/subscriptions/current`, { headers }),
      ]);
      setPlans(plansRes.data);
      setSubs(Array.isArray(subsRes.data) ? subsRes.data : []);
    } catch { } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCheckout = async (lookupKey) => {
    setCheckoutLoading(lookupKey);
    try {
      const res = await axios.post(`${API}/api/payments/checkout`, {
        lookup_key: lookupKey,
        origin_url: window.location.origin,
        org_id: user?.org_id,
      }, { headers });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur creation session de paiement");
    } finally {
      setCheckoutLoading(null);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#0099CC]" /></div>;

  const orgModules = user?.org?.active_modules || [];

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="subscription-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <CreditCard className="w-6 h-6 text-[#0099CC]" /> Mon abonnement
          </h1>
          <p className="text-sm text-[#64748b] mt-1">Gerez vos plans et modules actifs</p>
        </div>
        <div className="flex items-center gap-2 bg-[#0B0F19] border border-[#1E293B] rounded-lg px-3 py-1.5">
          <span className={`text-xs ${!yearly ? "text-white font-medium" : "text-[#64748b]"}`}>Mensuel</span>
          <button
            onClick={() => setYearly(!yearly)}
            className={`relative w-10 h-5 rounded-full transition-colors ${yearly ? "bg-[#10B981]" : "bg-[#2A3441]"}`}
            data-testid="billing-toggle"
          >
            <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${yearly ? "translate-x-5" : "translate-x-0.5"}`} />
          </button>
          <span className={`text-xs ${yearly ? "text-white font-medium" : "text-[#64748b]"}`}>Annuel</span>
        </div>
      </div>

      {subs.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-[#64748b] uppercase tracking-wider">Abonnements actifs</h2>
          {subs.map((sub, i) => {
            const meta = MODULE_META[sub.module] || {};
            const Icon = meta.icon || CreditCard;
            return (
              <div key={i} className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-4 flex items-center gap-4" data-testid={`active-sub-${sub.module}`}>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: (meta.color || "#0099CC") + "15" }}>
                  <Icon className="w-5 h-5" style={{ color: meta.color }} />
                </div>
                <div className="flex-1">
                  <p className="text-white font-medium">{meta.label} — {sub.plan?.charAt(0).toUpperCase() + sub.plan?.slice(1)}</p>
                  <p className="text-xs text-[#64748b]">Statut : <span className={sub.status === "active" ? "text-[#10B981]" : "text-[#F59E0B]"}>{sub.status}</span></p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${sub.status === "active" ? "bg-[#10B981]/20 text-[#10B981]" : "bg-[#F59E0B]/20 text-[#F59E0B]"}`}>
                  {sub.status === "active" ? "Actif" : sub.status}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {plans && orgModules.map(mod => {
        const modPlans = plans[mod];
        if (!modPlans) return null;
        const meta = MODULE_META[mod] || {};
        const currentSub = subs.find(s => s.module === mod);
        const currentPlan = currentSub?.plan || "free";

        return (
          <div key={mod} className="space-y-4">
            <h2 className="text-sm font-semibold uppercase tracking-wider flex items-center gap-2" style={{ color: meta.color }}>
              {meta.label}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {Object.entries(modPlans).map(([planKey, plan]) => {
                const PlanIcon = PLAN_ICONS[planKey] || Zap;
                const price = yearly ? plan.price_yearly : plan.price_monthly;
                const period = yearly ? "/an" : "/mois";
                const lookupKey = yearly ? plan.lookup_key_yearly : plan.lookup_key_monthly;
                const isCurrent = currentPlan === planKey;
                const isUpgrade = !isCurrent && planKey !== "free" && (planKey === "enterprise" || (planKey === "pro" && currentPlan === "free"));

                return (
                  <div
                    key={planKey}
                    className={`bg-[#060B14] border-2 rounded-xl p-5 transition-all ${isCurrent ? "border-opacity-50" : "border-[#1E293B]"}`}
                    style={isCurrent ? { borderColor: meta.color } : undefined}
                    data-testid={`sub-plan-${mod}-${planKey}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="flex items-center gap-1.5 text-sm font-semibold text-white">
                        <PlanIcon className="w-4 h-4" style={{ color: meta.color }} /> {plan.name}
                      </span>
                      {isCurrent && <span className="text-[10px] px-2 py-0.5 rounded-full font-bold" style={{ backgroundColor: meta.color + "20", color: meta.color }}>ACTUEL</span>}
                    </div>
                    <div className="mb-4">
                      <span className="text-2xl font-bold text-white">{price}€</span>
                      <span className="text-xs text-[#64748b]">{period}</span>
                    </div>
                    {isCurrent ? (
                      <Button disabled className="w-full bg-[#1E293B] text-[#64748b]" size="sm">
                        <Check className="w-4 h-4 mr-1" /> Plan actuel
                      </Button>
                    ) : isUpgrade && lookupKey ? (
                      <Button
                        onClick={() => handleCheckout(lookupKey)}
                        disabled={checkoutLoading === lookupKey}
                        className="w-full text-white" size="sm"
                        style={{ backgroundColor: meta.color }}
                        data-testid={`upgrade-btn-${mod}-${planKey}`}
                      >
                        {checkoutLoading === lookupKey ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-1" />}
                        Passer au {plan.name}
                      </Button>
                    ) : (
                      <Button disabled className="w-full bg-[#0B0F19] border border-[#1E293B] text-[#64748b]" size="sm">
                        {planKey === "free" ? "Inclus" : "Indisponible"}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
